/**
 * EmblaPhotoCarousel - Generic infinite loop photo carousel
 *
 * Features:
 * - Embla Carousel with infinite loop
 * - Navigation buttons
 * - Dot indicators
 * - Responsive design
 * - Generic and reusable
 * - Modal mode with full-image display and zoom
 * - Two-way binding support for ScrollSpy pattern
 */
import { useCallback, useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

interface CarouselImage {
  url: string;
  alt?: string;
}

interface EmblaPhotoCarouselProps {
  images: CarouselImage[];
  className?: string;
  /** Initial slide index */
  initialSlide?: number;
  /** Click handler for images */
  onImageClick?: (index: number) => void;
  /** Display mode: 'page' for cropped banner, 'modal' for full-image with zoom */
  mode?: "page" | "modal";
  /** Callback when slide changes (for ScrollSpy sync) */
  onSlideChange?: (index: number) => void;
  /** External control: programmatically scroll to this index (for tab-click navigation) */
  externalSlideIndex?: number;
}

export function EmblaPhotoCarousel({
  images,
  className = "",
  initialSlide = 0,
  onImageClick,
  mode = "page",
  onSlideChange,
  externalSlideIndex,
}: EmblaPhotoCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    startIndex: initialSlide,
    // Smooth animation settings
    duration: 25, // Smoother transition (lower = faster, 25 is smooth)
    skipSnaps: false, // Ensures snapping to each slide
  });

  const [selectedIndex, setSelectedIndex] = useState(initialSlide);
  const [isZoomed, setIsZoomed] = useState(false);

  // Track if we're currently doing a programmatic scroll to prevent feedback loop
  const isProgrammaticScroll = useRef(false);

  const scrollPrev = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (emblaApi) emblaApi.scrollPrev();
    },
    [emblaApi],
  );

  const scrollNext = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (emblaApi) emblaApi.scrollNext();
    },
    [emblaApi],
  );

  const scrollTo = useCallback(
    (index: number, e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    setSelectedIndex(newIndex);

    // Notify parent of slide change (for ScrollSpy sync)
    // Only notify if this wasn't triggered by programmatic scroll to avoid loop
    if (!isProgrammaticScroll.current && onSlideChange) {
      onSlideChange(newIndex);
    }
    isProgrammaticScroll.current = false;
  }, [emblaApi, onSlideChange]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Handle external slide index changes (tab-click navigation)
  useEffect(() => {
    if (
      emblaApi &&
      externalSlideIndex !== undefined &&
      externalSlideIndex !== selectedIndex
    ) {
      isProgrammaticScroll.current = true;
      emblaApi.scrollTo(externalSlideIndex);
    }
  }, [emblaApi, externalSlideIndex, selectedIndex]);

  // Reset zoom when navigating images in modal mode
  useEffect(() => {
    if (mode === "modal") {
      setIsZoomed(false);
    }
  }, [selectedIndex, mode]);

  const handleImageClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (mode === "modal") {
        // In modal mode, toggle zoom
        setIsZoomed(!isZoomed);
      } else if (onImageClick) {
        // In page mode, trigger parent click handler
        onImageClick(selectedIndex);
      }
    },
    [mode, isZoomed, onImageClick, selectedIndex],
  );

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className={`w-full relative ${className}`}>
      {/* Embla Viewport */}
      <div
        className={
          mode === "modal" ? "overflow-hidden" : "overflow-hidden rounded-xl"
        }
        ref={emblaRef}
      >
        <div className="flex transition-transform duration-300 ease-out">
          {images.map((image, index) => (
            <div key={index} className="flex-[0_0_100%] min-w-0">
              {mode === "page" ? (
                // Page mode: Wide banner with object-cover (current behavior)
                <div className="relative aspect-[21/9] bg-gray-100">
                  <img
                    src={image.url}
                    alt={image.alt || `Image ${index + 1}`}
                    className={`w-full h-full object-cover ${
                      onImageClick
                        ? "cursor-pointer hover:opacity-95 transition-opacity"
                        : ""
                    }`}
                    onClick={handleImageClick}
                    loading="lazy"
                  />
                </div>
              ) : (
                // Modal mode: Full image display with zoom
                <div className="flex items-center justify-center min-h-[85vh]">
                  <img
                    src={image.url}
                    alt={image.alt || `Image ${index + 1}`}
                    className={`
                      transition-all
                      ${
                        isZoomed
                          ? "cursor-zoom-out w-auto h-auto"
                          : "max-w-[95vw] max-h-[85vh] object-contain cursor-zoom-in"
                      }
                    `}
                    onClick={handleImageClick}
                    loading="lazy"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <button
            onClick={scrollPrev}
            className={`
              absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full 
              shadow-lg flex items-center justify-center transition-all hover:scale-110
              ${
                mode === "modal"
                  ? "bg-white/20 hover:bg-white/30 backdrop-blur-sm"
                  : "bg-white/90 hover:bg-white backdrop-blur-sm"
              }
            `}
            aria-label="Previous image"
          >
            <ChevronLeft
              className={`w-5 h-5 ${mode === "modal" ? "text-white" : "text-gray-800"}`}
            />
          </button>

          <button
            onClick={scrollNext}
            className={`
              absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full 
              shadow-lg flex items-center justify-center transition-all hover:scale-110
              ${
                mode === "modal"
                  ? "bg-white/20 hover:bg-white/30 backdrop-blur-sm"
                  : "bg-white/90 hover:bg-white backdrop-blur-sm"
              }
            `}
            aria-label="Next image"
          >
            <ChevronRight
              className={`w-5 h-5 ${mode === "modal" ? "text-white" : "text-gray-800"}`}
            />
          </button>
        </>
      )}

      {/* Zoom Indicator (Modal Mode Only) */}
      {mode === "modal" && (
        <div className="absolute top-4 left-4 z-10 bg-black/50 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center gap-2">
          {isZoomed ? (
            <>
              <ZoomOut className="w-4 h-4 text-white" />
              <span className="text-white text-sm">Click to zoom out</span>
            </>
          ) : (
            <>
              <ZoomIn className="w-4 h-4 text-white" />
              <span className="text-white text-sm">Click to zoom in</span>
            </>
          )}
        </div>
      )}

      {/* Dots Indicator */}
      {images.length > 1 && (
        <div
          className={`flex justify-center gap-2 ${mode === "modal" ? "mt-6" : "mt-4"}`}
        >
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => scrollTo(index, e)}
              className={`h-2 rounded-full transition-all ${
                index === selectedIndex
                  ? mode === "modal"
                    ? "w-8 bg-white"
                    : "w-8 bg-primary-600"
                  : mode === "modal"
                    ? "w-2 bg-white/50 hover:bg-white/70"
                    : "w-2 bg-gray-300 hover:bg-gray-400"
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Image Counter */}
      {images.length > 1 && (
        <div
          className={`text-center mt-2 text-sm ${mode === "modal" ? "text-white" : "text-gray-500"}`}
        >
          {selectedIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
}
